/**
 * SocialPostModal - Post staged photos to social media channels
 *
 * Allows users to post all staged photos from a property batch to
 * their connected social media platforms with one click.
 *
 * Includes full onboarding flow for new users:
 * 1. Assign brand slot (if no brand)
 * 2. Connect social accounts (if no accounts)
 * 3. Create and schedule posts
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, Send, Loader2, CheckCircle, AlertCircle,
    Instagram, Facebook, Twitter, Linkedin,
    Calendar, Clock, Image as ImageIcon, Sparkles, AlertTriangle,
    Rocket, Link2, ExternalLink, RefreshCw, Youtube, BarChart3, Users
} from 'lucide-react';
import PropTypes from 'prop-types';
import { getStoredTokens, BASE_URL } from '../api/client';
import { socialSchedulerService } from '../services/SocialSchedulerService';

// Platform icons mapping
const PLATFORM_ICONS = {
    instagram: Instagram,
    facebook: Facebook,
    twitter: Twitter,
    linkedin: Linkedin,
    youtube: Youtube,
};

// Platform colors
const PLATFORM_COLORS = {
    instagram: 'from-pink-500 to-purple-500',
    facebook: 'from-blue-600 to-blue-700',
    twitter: 'from-sky-400 to-sky-500',
    linkedin: 'from-blue-700 to-blue-800',
    tiktok: 'from-zinc-800 to-zinc-900',
    youtube: 'from-red-500 to-red-600',
    pinterest: 'from-red-600 to-red-700',
    threads: 'from-zinc-700 to-zinc-800',
};

// Onboarding step enum
const ONBOARDING_STEPS = {
    LOADING: 'loading',
    ASSIGN_BRAND: 'assign_brand',
    CONNECT_ACCOUNTS: 'connect_accounts',
    READY: 'ready',
};

/**
 * Fetch social config for staging
 */
const fetchSocialConfig = async () => {
    const { accessToken } = getStoredTokens();
    const response = await fetch(`${BASE_URL}/api/staging/social/config`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch social config');
    }

    return response.json();
};

// ============================================================================
// Onboarding: Assign Brand Screen
// ============================================================================
const AssignBrandScreen = ({ onAssignBrand, isLoading, error }) => (
    <div className="flex flex-col items-center justify-center py-8 px-4">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Rocket className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2 text-center">
            Welcome to Social Posting
        </h3>
        <p className="text-slate-500 dark:text-slate-400 mb-6 text-center max-w-md">
            Post your staged property photos directly to Instagram, Facebook, LinkedIn, and more.
            Let's set up your social media account first.
        </p>
        <div className="grid grid-cols-3 gap-3 mb-6 w-full max-w-sm">
            <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-700/50 text-center">
                <Calendar className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                <p className="text-xs text-slate-600 dark:text-slate-300">Schedule</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-700/50 text-center">
                <BarChart3 className="w-5 h-5 mx-auto mb-1 text-green-500" />
                <p className="text-xs text-slate-600 dark:text-slate-300">Analytics</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-700/50 text-center">
                <Users className="w-5 h-5 mx-auto mb-1 text-purple-500" />
                <p className="text-xs text-slate-600 dark:text-slate-300">Multi-Platform</p>
            </div>
        </div>
        {error && (
            <div className="w-full max-w-sm mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-300 text-center">{error}</p>
            </div>
        )}
        <button
            onClick={onAssignBrand}
            disabled={isLoading}
            className="w-full max-w-sm py-3 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
            {isLoading ? (
                <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Setting up...
                </>
            ) : (
                <>
                    <Sparkles className="w-5 h-5" />
                    Get Started
                </>
            )}
        </button>
    </div>
);

// ============================================================================
// Onboarding: Connect Accounts Screen
// ============================================================================
const ConnectAccountsScreen = ({ brand, onRefresh, isLoading, error }) => {
    // Open Metricool to connect social accounts
    const handleConnectAccounts = () => {
        if (brand?.metricoolBlogId) {
            const url = `https://app.metricool.com/${brand.metricoolBlogId}/settings/integrations`;
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div className="flex flex-col items-center justify-center py-6 px-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Link2 className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2 text-center">
                Connect Your Social Accounts
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-4 text-center max-w-md">
                Link your Instagram, Facebook, LinkedIn, and other social accounts to start posting your staged photos.
            </p>

            {/* Instructions */}
            <div className="w-full max-w-md mb-4 space-y-2">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-700/30">
                    <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">1</div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">Click "Connect Accounts" to open Metricool</p>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-700/30">
                    <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">2</div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">Link your social media accounts</p>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-700/30">
                    <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">3</div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">Click "Refresh" to sync your accounts</p>
                </div>
            </div>

            {/* Platform icons */}
            <div className="flex gap-2 mb-4">
                {[Instagram, Facebook, Linkedin, Twitter, Youtube].map((Icon, i) => (
                    <div key={i} className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                    </div>
                ))}
            </div>

            {/* Buttons */}
            <div className="w-full max-w-sm space-y-2">
                <button
                    onClick={handleConnectAccounts}
                    className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all flex items-center justify-center gap-2"
                >
                    <ExternalLink className="w-5 h-5" />
                    Connect Accounts
                </button>
                <button
                    onClick={onRefresh}
                    disabled={isLoading}
                    className="w-full py-3 px-6 bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-white rounded-xl hover:bg-slate-300 dark:hover:bg-slate-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh Accounts
                </button>
            </div>
            {error && (
                <div className="w-full max-w-sm mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-700 dark:text-red-300 text-center">{error}</p>
                </div>
            )}
            <p className="text-xs text-slate-400 mt-3 text-center">
                After connecting, click Refresh to sync your accounts
            </p>
        </div>
    );
};

/**
 * Get staged photos from a batch
 */
const fetchBatchPhotos = async (batchId) => {
    const { accessToken } = getStoredTokens();
    const response = await fetch(`${BASE_URL}/api/staging/social/batch/${batchId}/photos`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch batch photos');
    }

    return response.json();
};

/**
 * Create social post with staged photos
 */
const createSocialPost = async (data) => {
    const { accessToken } = getStoredTokens();
    const response = await fetch(`${BASE_URL}/api/staging/social/post`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail?.message || error.message || 'Failed to create post');
    }

    return response.json();
};

const SocialPostModal = ({
    isOpen,
    onClose,
    batchId,
    propertyName,
    propertyAddress,
    onSuccess,
}) => {
    // State
    const [loading, setLoading] = useState(true);
    const [posting, setPosting] = useState(false);
    const [config, setConfig] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [caption, setCaption] = useState('');
    const [scheduleMode, setScheduleMode] = useState('now'); // 'now' or 'schedule'
    const [scheduledDate, setScheduledDate] = useState('');
    const [scheduledTime, setScheduledTime] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Onboarding state
    const [onboardingStep, setOnboardingStep] = useState(ONBOARDING_STEPS.LOADING);
    const [brand, setBrand] = useState(null);
    const [assigningBrand, setAssigningBrand] = useState(false);
    const [refreshingAccounts, setRefreshingAccounts] = useState(false);

    // Determine onboarding step based on config
    const determineOnboardingStep = useCallback((configData) => {
        console.log('[SocialPostModal] Determining onboarding step:', configData);

        // If social scheduler not available (wrong tier)
        if (!configData?.available) {
            return ONBOARDING_STEPS.READY; // Will show upgrade prompt
        }

        // If no brand assigned
        if (!configData?.currentBrand) {
            return ONBOARDING_STEPS.ASSIGN_BRAND;
        }

        // If no platforms connected
        if (!configData?.platforms || configData.platforms.length === 0) {
            return ONBOARDING_STEPS.CONNECT_ACCOUNTS;
        }

        // All set, ready to post
        return ONBOARDING_STEPS.READY;
    }, []);

    // Load config and photos
    useEffect(() => {
        if (!isOpen || !batchId) return;

        const loadData = async () => {
            setLoading(true);
            setError(null);
            setSuccess(null);
            setOnboardingStep(ONBOARDING_STEPS.LOADING);

            try {
                console.log('[SocialPostModal] Fetching config and photos for batch:', batchId);

                const [configData, photosData] = await Promise.all([
                    fetchSocialConfig(),
                    fetchBatchPhotos(batchId),
                ]);

                console.log('[SocialPostModal] Config response:', configData);
                console.log('[SocialPostModal] Photos response:', photosData);

                setConfig(configData);
                setPhotos(photosData.photos || []);

                // Set brand if available
                if (configData?.currentBrand) {
                    setBrand(configData.currentBrand);
                }

                // Determine onboarding step
                const step = determineOnboardingStep(configData);
                setOnboardingStep(step);
                console.log('[SocialPostModal] Onboarding step:', step);

                // Auto-select all connected platforms
                if (configData.platforms?.length > 0) {
                    setSelectedPlatforms(configData.platforms.map(p => p.id));
                }

                // Generate default caption
                const defaultCaption = generateDefaultCaption(propertyName, propertyAddress, photosData.photos?.length || 0);
                setCaption(defaultCaption);

            } catch (err) {
                console.error('[SocialPostModal] Load error:', err);
                setError(err.message);
                setOnboardingStep(ONBOARDING_STEPS.READY);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [isOpen, batchId, propertyName, propertyAddress, determineOnboardingStep]);

    // Handle brand assignment
    const handleAssignBrand = async () => {
        setAssigningBrand(true);
        setError(null);

        try {
            console.log('[SocialPostModal] Assigning brand...');
            const result = await socialSchedulerService.assignBrand();
            console.log('[SocialPostModal] Brand assigned:', result);

            // Update brand state
            setBrand(result);

            // Move to connect accounts step
            setOnboardingStep(ONBOARDING_STEPS.CONNECT_ACCOUNTS);

        } catch (err) {
            console.error('[SocialPostModal] Brand assignment error:', err);
            setError(err.message || 'Failed to set up your account. Please try again.');
        } finally {
            setAssigningBrand(false);
        }
    };

    // Handle refresh accounts
    const handleRefreshAccounts = async () => {
        setRefreshingAccounts(true);
        setError(null);

        try {
            console.log('[SocialPostModal] Refreshing accounts...');

            // Refresh accounts from Metricool
            await socialSchedulerService.refreshAccounts();

            // Re-fetch config to get updated platforms
            const configData = await fetchSocialConfig();
            console.log('[SocialPostModal] Refreshed config:', configData);

            setConfig(configData);

            if (configData?.currentBrand) {
                setBrand(configData.currentBrand);
            }

            // Check if we now have connected accounts
            if (configData?.platforms?.length > 0) {
                setSelectedPlatforms(configData.platforms.map(p => p.id));
                setOnboardingStep(ONBOARDING_STEPS.READY);
            }

        } catch (err) {
            console.error('[SocialPostModal] Refresh accounts error:', err);
            setError(err.message || 'Failed to refresh accounts. Please try again.');
        } finally {
            setRefreshingAccounts(false);
        }
    };

    // Generate default caption
    const generateDefaultCaption = (name, address, photoCount) => {
        const property = name || address || 'this property';
        const photoText = photoCount > 1 ? `${photoCount} beautifully staged photos` : 'a beautifully staged photo';
        return `Check out ${photoText} of ${property}! Ready to make this house your home? Contact us today for a showing.

#RealEstate #PropertyForSale #HomeDesign #VirtualStaging #DreamHome`;
    };

    // Toggle platform selection
    const togglePlatform = useCallback((platformId) => {
        setSelectedPlatforms(prev => {
            if (prev.includes(platformId)) {
                return prev.filter(p => p !== platformId);
            }
            return [...prev, platformId];
        });
    }, []);

    // Calculate credits
    const calculateCredits = useCallback(() => {
        if (!config) return 0;
        const baseCredits = config.creditsPerPost || 50;
        const extraCredits = config.creditsPerExtraPlatform || 25;
        const platformCount = selectedPlatforms.length;
        if (platformCount === 0) return 0;
        return baseCredits + (Math.max(0, platformCount - 1) * extraCredits);
    }, [config, selectedPlatforms]);

    // Handle post
    const handlePost = async () => {
        if (selectedPlatforms.length === 0) {
            setError('Please select at least one platform');
            return;
        }

        if (!caption.trim()) {
            setError('Please enter a caption');
            return;
        }

        setPosting(true);
        setError(null);

        try {
            // Build scheduled time if needed
            let scheduledTimeValue = null;
            if (scheduleMode === 'schedule' && scheduledDate && scheduledTime) {
                scheduledTimeValue = `${scheduledDate}T${scheduledTime}:00`;
            }

            const result = await createSocialPost({
                batchId,
                platforms: selectedPlatforms,
                caption: caption.trim(),
                scheduledTime: scheduledTimeValue,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago',
            });

            setSuccess(result);

            // Notify parent after delay
            setTimeout(() => {
                if (onSuccess) {
                    onSuccess(result);
                }
                onClose();
            }, 2000);

        } catch (err) {
            console.error('[SocialPostModal] Post error:', err);
            setError(err.message);
        } finally {
            setPosting(false);
        }
    };

    // Reset state on close
    const handleClose = () => {
        setError(null);
        setSuccess(null);
        setSelectedPlatforms([]);
        setCaption('');
        setScheduleMode('now');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                onClick={handleClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                <Send className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
                                    Post to Social Media
                                </h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Share staged photos with your audience
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-slate-500" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-4 overflow-y-auto max-h-[calc(90vh-180px)]">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 text-violet-500 animate-spin mb-3" />
                                <p className="text-slate-500 dark:text-slate-400">Loading...</p>
                            </div>
                        ) : success ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <CheckCircle className="w-16 h-16 text-emerald-500 mb-4" />
                                <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
                                    Post Created!
                                </h3>
                                <p className="text-slate-500 dark:text-slate-400 text-center">
                                    {success.message}
                                </p>
                                <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2">
                                    {success.creditsCharged} credits used
                                </p>
                            </div>
                        ) : error && !config ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
                                <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
                                    Failed to Load
                                </h3>
                                <p className="text-slate-500 dark:text-slate-400 text-center max-w-md">
                                    {error}
                                </p>
                            </div>
                        ) : !config?.available ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <AlertTriangle className="w-16 h-16 text-amber-500 mb-4" />
                                <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
                                    Social Scheduler Not Available
                                </h3>
                                <p className="text-slate-500 dark:text-slate-400 text-center max-w-md">
                                    Social posting requires an Ultra or Enterprise subscription.
                                    Upgrade to unlock this feature.
                                </p>
                            </div>
                        ) : onboardingStep === ONBOARDING_STEPS.ASSIGN_BRAND ? (
                            /* Onboarding Step 1: Assign Brand */
                            <AssignBrandScreen
                                onAssignBrand={handleAssignBrand}
                                isLoading={assigningBrand}
                                error={error}
                            />
                        ) : onboardingStep === ONBOARDING_STEPS.CONNECT_ACCOUNTS ? (
                            /* Onboarding Step 2: Connect Accounts */
                            <ConnectAccountsScreen
                                brand={brand}
                                onRefresh={handleRefreshAccounts}
                                isLoading={refreshingAccounts}
                                error={error}
                            />
                        ) : (
                            <div className="space-y-6">
                                {/* Error display */}
                                {error && (
                                    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                                        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                                    </div>
                                )}

                                {/* Photos Preview */}
                                <div>
                                    <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                                        <ImageIcon className="w-4 h-4" />
                                        Photos to Post ({photos.length})
                                    </h3>
                                    <div className="flex gap-2 overflow-x-auto pb-2">
                                        {photos.slice(0, 10).map((photo, idx) => (
                                            <div
                                                key={photo.photoId || `photo-${idx}`}
                                                className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700"
                                            >
                                                <img
                                                    src={photo.stagedUrl || photo.thumbnailUrl}
                                                    alt={`Staged photo ${idx + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                                                    <span className="text-[10px] text-white font-medium">
                                                        {photo.roomType || 'Room'}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                        {photos.length > 10 && (
                                            <div className="flex-shrink-0 w-20 h-20 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                                <span className="text-sm text-slate-500 dark:text-slate-400">
                                                    +{photos.length - 10}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Platform Selection */}
                                <div>
                                    <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                                        Select Platforms
                                    </h3>
                                    {config.platforms?.length === 0 ? (
                                        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                            <p className="text-sm text-amber-700 dark:text-amber-300">
                                                No social accounts connected. Please connect your accounts in the Social Scheduler settings.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                            {config.platforms?.map(platform => {
                                                const Icon = PLATFORM_ICONS[platform.id] || Send;
                                                const isSelected = selectedPlatforms.includes(platform.id);
                                                const colorClass = PLATFORM_COLORS[platform.id] || 'from-slate-500 to-slate-600';

                                                return (
                                                    <button
                                                        key={platform.id}
                                                        onClick={() => togglePlatform(platform.id)}
                                                        className={`relative p-3 rounded-xl border-2 transition-all ${
                                                            isSelected
                                                                ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                                                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                                                        }`}
                                                    >
                                                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center mx-auto mb-2`}>
                                                            <Icon className="w-5 h-5 text-white" />
                                                        </div>
                                                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300 text-center capitalize">
                                                            {platform.name || platform.id}
                                                        </p>
                                                        {isSelected && (
                                                            <div className="absolute top-1 right-1 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                                                                <CheckCircle className="w-3 h-3 text-white" />
                                                            </div>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Caption */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Caption
                                        </h3>
                                        <span className={`text-xs ${caption.length > 2000 ? 'text-red-500' : 'text-slate-400'}`}>
                                            {caption.length}/2200
                                        </span>
                                    </div>
                                    <textarea
                                        value={caption}
                                        onChange={e => setCaption(e.target.value)}
                                        placeholder="Write a caption for your post..."
                                        rows={5}
                                        maxLength={2200}
                                        className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                                    />
                                    <button
                                        onClick={() => setCaption(generateDefaultCaption(propertyName, propertyAddress, photos.length))}
                                        className="mt-2 flex items-center gap-1.5 text-xs text-violet-600 dark:text-violet-400 hover:underline"
                                    >
                                        <Sparkles className="w-3 h-3" />
                                        Reset to default caption
                                    </button>
                                </div>

                                {/* Schedule Options */}
                                <div>
                                    <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                                        When to Post
                                    </h3>
                                    <div className="flex gap-3 mb-3">
                                        <button
                                            onClick={() => setScheduleMode('now')}
                                            className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                                                scheduleMode === 'now'
                                                    ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                                                    : 'border-slate-200 dark:border-slate-700'
                                            }`}
                                        >
                                            <Send className="w-4 h-4" />
                                            <span className="text-sm font-medium">Post Now</span>
                                        </button>
                                        <button
                                            onClick={() => setScheduleMode('schedule')}
                                            className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                                                scheduleMode === 'schedule'
                                                    ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                                                    : 'border-slate-200 dark:border-slate-700'
                                            }`}
                                        >
                                            <Calendar className="w-4 h-4" />
                                            <span className="text-sm font-medium">Schedule</span>
                                        </button>
                                    </div>

                                    {scheduleMode === 'schedule' && (
                                        <div className="flex gap-3">
                                            <div className="flex-1">
                                                <label className="block text-xs text-slate-500 mb-1">Date</label>
                                                <input
                                                    type="date"
                                                    value={scheduledDate}
                                                    onChange={e => setScheduledDate(e.target.value)}
                                                    min={new Date().toISOString().split('T')[0]}
                                                    className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-white"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <label className="block text-xs text-slate-500 mb-1">Time</label>
                                                <input
                                                    type="time"
                                                    value={scheduledTime}
                                                    onChange={e => setScheduledTime(e.target.value)}
                                                    className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-white"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Credit Cost */}
                                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-xl">
                                    <div>
                                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Credit Cost
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            {selectedPlatforms.length} platform{selectedPlatforms.length !== 1 ? 's' : ''} selected
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">
                                            {calculateCredits()}
                                        </p>
                                        <p className="text-xs text-slate-500">credits</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {!loading && !success && config?.available && onboardingStep === ONBOARDING_STEPS.READY && (
                        <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePost}
                                disabled={posting || selectedPlatforms.length === 0 || !caption.trim()}
                                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-medium rounded-xl transition-all"
                            >
                                {posting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Posting...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        {scheduleMode === 'now' ? 'Post Now' : 'Schedule Post'}
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                    {/* Onboarding Footer - Show Cancel only */}
                    {!loading && !success && config?.available && onboardingStep !== ONBOARDING_STEPS.READY && (
                        <div className="flex items-center justify-center p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

SocialPostModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    batchId: PropTypes.string.isRequired,
    propertyName: PropTypes.string,
    propertyAddress: PropTypes.string,
    onSuccess: PropTypes.func,
};

export default SocialPostModal;
